<script setup lang="ts">
import type { PropType } from 'vue'
import {ref, watchPostEffect, watch, computed} from 'vue'
import { useRoute } from 'vue-router'
import { SidebarType } from '/@src/constants'
import { activePanel } from '/@src/state/activePanelState'
import { pageTitle } from '/@src/state/sidebarLayoutState'
import useUserSession from '/@src/composable/useUserSession'

type SidebarTheme =
  | 'default'
  | 'color'
  | 'color-curved'
  | 'curved'
  | 'float'
  | 'labels'
  | 'labels-hover'

type SidebarType =
  | 'default'
  | 'color'
  | 'color-curved'
  | 'curved'
  | 'float'
  | 'labels'
  | 'labels-hover'

export type PageSize = 'sm'|'md'|'lg'|'xl'|'2xl'|'full'

const props = defineProps({
  theme: {
    type: String as PropType<SidebarTheme>,
    default: 'default',
  },
  defaultSidebar: {
    type: String,
    default: 'dashboard',
  },
  closeOnChange: {
    type: Boolean,
    default: false,
  },
  openOnMounted: {
    type: Boolean,
    default: false,
  },
  nowrap: {
    type: Boolean,
    default: false,
  },
})

const route = useRoute()
const isMobileSidebarOpen = ref(false)
const isDesktopSidebarOpen = ref(props.openOnMounted)
const activeMobileSubsidebar = ref(props.defaultSidebar)
const userSession = useUserSession()

function switchSidebar(id: string) {
  if (id === activeMobileSubsidebar.value) {
    isDesktopSidebarOpen.value = !isDesktopSidebarOpen.value
  } else {
    isDesktopSidebarOpen.value = true
    activeMobileSubsidebar.value = id
  }
}

/**
 * watchPostEffect callback will be executed each time dependent reactive values has changed
 */
watchPostEffect(() => {
  const isOpen = isDesktopSidebarOpen.value
  const wrappers = document.querySelectorAll('.view-wrapper')

  wrappers.forEach((wrapper) => {
    if (isOpen === false) {
      wrapper.classList.remove('is-pushed-full')
    } else if (!wrapper.classList.contains('is-pushed-full')) {
      wrapper.classList.add('is-pushed-full')
    }
  })
})
watch(
  () => route.fullPath,
  () => {
    isMobileSidebarOpen.value = false

    if (props.closeOnChange && isDesktopSidebarOpen.value) {
      isDesktopSidebarOpen.value = false
    }
  }
)

/**
 * 页面宽度等动态设置
 */
const pageClasses = computed(() => {
  const pageSize = route.meta?.pageSize as PageSize
  switch (pageSize) {
    case 'sm':
      return '!max-w-1024'
    case 'md':
      return '!max-w-1200'
    case 'lg':
      return '!max-w-1600'
    case 'xl':
      return '!max-w-1800'
    case '2xl':
      return '!max-w-2160'
    case 'full':
      return '!max-w-full'
    default:
      return ''
  }
})

</script>

<template>
  <div class="sidebar-layout">
    <div class="app-overlay"></div>

    <!-- Mobile navigation -->
    <MobileNavbar
      :is-open="isMobileSidebarOpen"
      @toggle="isMobileSidebarOpen = !isMobileSidebarOpen"
    >
      <template #brand>
        <RouterLink :to="{ name: 'index' }" class="navbar-item is-brand">
          <AnimatedLogo width="38px" height="38px" />
        </RouterLink>

        <div class="brand-end">
          <!-- TODO -->
          <!--          <NotificationsMobileDropdown />-->
          <WisburgUserProfileDropdown />
        </div>
      </template>
    </MobileNavbar>

    <!-- Mobile sidebar links -->
    <MobileSidebar
      :is-open="isMobileSidebarOpen"
      @toggle="isMobileSidebarOpen = !isMobileSidebarOpen"
    >
      <template #links>
        <li>
          <a
            :class="[
              activeMobileSubsidebar === SidebarType.CONTENT && 'is-active',
            ]"
            @click="activeMobileSubsidebar = SidebarType.CONTENT"
          >
            <i
              aria-hidden="true"
              class="iconify"
              data-icon="feather:activity"
            ></i>
          </a>
        </li>
        <li v-if="userSession.isInternalAdmin">
          <a
            :class="[
              activeMobileSubsidebar === SidebarType.USER && 'is-active',
            ]"
            @click="activeMobileSubsidebar = SidebarType.USER"
          >
            <i aria-hidden="true" class="iconify" data-icon="feather:users"></i>
          </a>
        </li>
        <li
          v-if="userSession.isInternalAdmin"
          :class="[
            activeMobileSubsidebar === SidebarType.FINANCE && 'is-active',
          ]"
          @click="activeMobileSubsidebar = SidebarType.FINANCE"
        >
          <a>
            <i
              aria-hidden="true"
              class="iconify"
              data-icon="feather:dollar-sign"
            ></i>
          </a>
        </li>
        <li
          v-if="userSession.isInternalAdmin"
          :class="[
            activeMobileSubsidebar === SidebarType.OPERATION && 'is-active',
          ]"
          @click="activeMobileSubsidebar = SidebarType.OPERATION"
        >
          <a>
            <i aria-hidden="true" class="iconify" data-icon="feather:cpu"></i>
          </a>
        </li>
      </template>

      <template #bottom-links>
        <!--        <li>-->
        <!--          <a @click="activePanel = 'search'">-->
        <!--            <i-->
        <!--              aria-hidden="true"-->
        <!--              class="iconify"-->
        <!--              data-icon="feather:search"-->
        <!--            ></i>-->
        <!--          </a>-->
        <!--        </li>-->
        <li>
          <a href="#">
            <i
              aria-hidden="true"
              class="iconify"
              data-icon="feather:settings"
            ></i>
          </a>
        </li>
      </template>
    </MobileSidebar>

    <!-- Mobile subsidebar links -->
    <transition name="slide-x">
      <ContentsMobileSubsidebar
        v-if="
          isMobileSidebarOpen && activeMobileSubsidebar === SidebarType.CONTENT
        "
      />
      <UsersMobileSubsidebar
        v-else-if="
          isMobileSidebarOpen && activeMobileSubsidebar === SidebarType.USER
        "
      />
      <FinanceMobileSubsidebar
        v-else-if="
          isMobileSidebarOpen && activeMobileSubsidebar === SidebarType.FINANCE
        "
      />
      <OperationMobileSubsidebar
        v-else-if="
          isMobileSidebarOpen &&
          activeMobileSubsidebar === SidebarType.OPERATION
        "
      />
      <MessageMobileSubsidebar
        v-else-if="
          isMobileSidebarOpen && activeMobileSubsidebar === SidebarType.MESSAGE
        "
      />
    </transition>

    <!-- Desktop navigation -->
    <CircularMenu />

    <Sidebar :theme="props.theme" :is-open="isDesktopSidebarOpen">
      <template #links>
        <!-- Dashboards -->
        <li>
          <a
            :class="[
              activeMobileSubsidebar === SidebarType.CONTENT && 'is-active',
            ]"
            data-content="Dashboards"
            @click="switchSidebar(SidebarType.CONTENT)"
          >
            <i
              aria-hidden="true"
              class="iconify sidebar-svg"
              data-icon="feather:activity"
            ></i>
          </a>
        </li>

        <!-- 用户 -->
        <li v-if="userSession.isInternalAdmin">
          <a
            :class="[
              activeMobileSubsidebar === SidebarType.USER && 'is-active',
            ]"
            data-content="Users"
            @click="switchSidebar(SidebarType.USER)"
          >
            <i
              aria-hidden="true"
              class="iconify sidebar-svg"
              data-icon="feather:users"
            ></i>
          </a>
        </li>

        <!-- 财务 -->
        <li v-if="userSession.isInternalAdmin">
          <a
            :class="[
              activeMobileSubsidebar === SidebarType.FINANCE && 'is-active',
            ]"
            data-content="Elements"
            @click="switchSidebar(SidebarType.FINANCE)"
          >
            <i
              aria-hidden="true"
              class="iconify sidebar-svg"
              data-icon="feather:dollar-sign"
            ></i>
          </a>
        </li>

        <!-- 运维 -->
        <li v-if="userSession.isInternalAdmin">
          <a
            :class="[
              activeMobileSubsidebar === SidebarType.OPERATION && 'is-active',
            ]"
            data-content="Components"
            @click="switchSidebar(SidebarType.OPERATION)"
          >
            <i
              aria-hidden="true"
              class="iconify sidebar-svg"
              data-icon="feather:cpu"
            ></i>
          </a>
        </li>

        <li v-if="userSession.isInternalAdmin">
          <a
            :class="[
              activeMobileSubsidebar === SidebarType.MESSAGE && 'is-active',
            ]"
            data-content="Components"
            @click="switchSidebar(SidebarType.MESSAGE)"
          >
            <i
              aria-hidden="true"
              class="iconify sidebar-svg"
              data-icon="feather:message-circle"
            ></i>
          </a>
        </li>

        <!-- Messaging -->
        <!--        <li>-->
        <!--          <RouterLink-->
        <!--            id="open-messages"-->
        <!--            :to="{ name: 'message' }"-->
        <!--            data-content="Messaging"-->
        <!--          >-->
        <!--            <i-->
        <!--              aria-hidden="true"-->
        <!--              class="iconify sidebar-svg"-->
        <!--              data-icon="feather:message-circle"-->
        <!--            ></i>-->
        <!--          </RouterLink>-->
        <!--        </li>-->
      </template>

      <template #bottom-links>
        <!-- Search -->
        <!--        <li class="right-panel-trigger">-->
        <!--          <a data-content="Search" @click="activePanel = 'search'">-->
        <!--            <i-->
        <!--              aria-hidden="true"-->
        <!--              class="iconify sidebar-svg"-->
        <!--              data-icon="feather-search"-->
        <!--            />-->
        <!--          </a>-->
        <!--          <a class="is-hidden is-inactive" @click="activePanel = 'none'">-->
        <!--            <i-->
        <!--              aria-hidden="true"-->
        <!--              class="iconify sidebar-svg"-->
        <!--              data-icon="feather-x"-->
        <!--            />-->
        <!--          </a>-->
        <!--        </li>-->

        <!-- Settings -->
        <li v-if="userSession.user" class="is-hidden-touch">
          <RouterLink
            id="open-settings"
            :to="{ path: `/users/profile/${userSession.user.id}/settings` }"
            data-content="Settings"
          >
            <i
              aria-hidden="true"
              class="iconify sidebar-svg"
              data-icon="feather:settings"
            ></i>
          </RouterLink>
        </li>

        <!-- Profile Dropdown -->
        <li>
          <WisburgUserProfileDropdown up />
        </li>
      </template>
    </Sidebar>

    <transition name="slide-x">
      <ContentsSubsidebar
        v-if="
          isDesktopSidebarOpen && activeMobileSubsidebar === SidebarType.CONTENT
        "
        @close="isDesktopSidebarOpen = false"
      />
      <UsersSubsidebar
        v-else-if="
          isDesktopSidebarOpen && activeMobileSubsidebar === SidebarType.USER
        "
        @close="isDesktopSidebarOpen = false"
      />
      <FinanceSubsidebar
        v-else-if="
          isDesktopSidebarOpen && activeMobileSubsidebar === SidebarType.FINANCE
        "
        @close="isDesktopSidebarOpen = false"
      />
      <OperationSubsidebar
        v-else-if="
          isDesktopSidebarOpen &&
          activeMobileSubsidebar === SidebarType.OPERATION
        "
        @close="isDesktopSidebarOpen = false"
      />
      <MessageSubsidebar
        v-else-if="
          isDesktopSidebarOpen && activeMobileSubsidebar === SidebarType.MESSAGE
        "
        @close="isDesktopSidebarOpen = false"
      />
    </transition>

    <LanguagesPanel />
<!--    <ActivityPanel />-->
    <SearchPanel />
    <TaskPanel />

    <div class="view-wrapper">
<!--      <div class="page-content-wrapper" :class="[isWidePage&&'!max-w-[1500px] md:!max-w-[1800px]']">-->
      <div class="page-content-wrapper" :class="pageClasses">
        <template v-if="props.nowrap">
          <slot></slot>
        </template>
        <div v-else class="page-content is-relative">
          <div class="page-title has-text-centered">
            <!-- Sidebar Trigger -->
            <div
              class="vuero-hamburger nav-trigger push-resize"
              @click="isDesktopSidebarOpen = !isDesktopSidebarOpen"
            >
              <span class="menu-toggle has-chevron">
                <span
                  :class="[isDesktopSidebarOpen && 'active']"
                  class="icon-box-toggle"
                >
                  <span class="rotate">
                    <i aria-hidden="true" class="icon-line-top"></i>
                    <i aria-hidden="true" class="icon-line-center"></i>
                    <i aria-hidden="true" class="icon-line-bottom"></i>
                  </span>
                </span>
              </span>
            </div>

            <div class="title-wrap">
              <h1 class="title is-4">{{ pageTitle }}</h1>
            </div>

            <Toolbar class="desktop-toolbar">
              <!--              <ToolbarNotification />-->

              <!--              <a-->
              <!--                class="toolbar-link right-panel-trigger"-->
              <!--                @click="activePanel = 'activity'"-->
              <!--              >-->
              <!--                <i-->
              <!--                  aria-hidden="true"-->
              <!--                  class="iconify"-->
              <!--                  data-icon="feather:grid"-->
              <!--                ></i>-->
              <!--              </a>-->
            </Toolbar>
          </div>

          <slot></slot>
        </div>
      </div>
    </div>
  </div>
</template>
